<template>
  <div id="map-terrain-container" class="map">
    <!-- custom popup component -->
    <Popup />

    <!-- navigation control -->
    <ControlFinal v-show="step === '7-2'" />

    <!-- legends -->
    <LegendFinal v-show="step === '7-2'" />

    <!-- ui instruction -->
    <!-- <PageInstruction v-show="step === '7-2'" /> -->
  </div>
</template>

<script>
import * as d3 from 'd3';
import { mapGetters } from 'vuex';

import mapServices from '@/services/map';

// components
// import PageInstruction from '@/components/scrolly/PageInstruction.vue';

import Popup from '@/components/ui/map/Popup.vue';
import ControlFinal from '@/components/ui/map/ControlFinal.vue';
import LegendFinal from '@/components/ui/map/LegendFinal.vue';

export default {
  name: 'MapTerrain',
  components: {
    Popup,
    ControlFinal,
    LegendFinal,
  },
  data() {
    return {
      map: null,
      defaultOptions: {
        container: 'map-terrain-container',
        style: process.env.VUE_APP_MAPBOX_STYLE_TERRAIN,
        center: [66.58, 33.49],
        zoom: 2.41,
        minZoom: 2.41,
        maxZoom: 7,
      },
    };
  },
  computed: {
    ...mapGetters('view', ['currentPage', 'step', 'cameraView']),
    ...mapGetters('viewFinal', ['zoom', 'railroads', 'oilpipelines', 'gaspipelines', 'ports']),
    ...mapGetters('mapState', ['mapOptions', 'mapLayers']),
  },
  watch: {
    currentPage(newVal, oldVal) {
      this.handleSectionPage(newVal, oldVal);
    },
    step(newVal, oldVal) {
      this.handleSectionStep(newVal, oldVal);
    },
    zoom(newVal) {
      d3.select('#map-terrain-container .mapboxgl-custom-popup').style('display', 'none');

      // zoom map
      this.map.zoomTo(newVal, 2);
    },
    railroads(newVal) {
      this.handleLayerVisibility('railroads', newVal);
    },
    oilpipelines(newVal) {
      this.handleLayerVisibility('oilpipelines', newVal);
    },
    gaspipelines(newVal) {
      this.handleLayerVisibility('gaspipelines', newVal);
    },
    ports(newVal) {
      this.handleLayerVisibility('ports', newVal);
    },
  },
  mounted() {
    // init Mapbox
    this.map = mapServices.createMap(this.defaultOptions);
  },
  methods: {
    handleSectionPage(currentPage, previousPage) {
      switch (previousPage) {
        case 7:
          this.cleanMap();
          break;

        default:
          break;
      }

      switch (currentPage) {
        case 7:
          this.loadMapLayers();
          break;

        default:
          break;
      }
    },
    handleSectionStep(currentStep, previousStep) {
      switch (previousStep) {
        case '7-2':
          this.loadMapUIAndEvents(false);
          break;

        default:
          break;
      }
      switch (currentStep) {
        case '7-2':
          // add mapbox nav control
          this.loadMapUIAndEvents();
          break;

        default:
          break;
      }
    },
    cleanMap() {
      this.map.removeLayer('brirailroads');
      this.map.removeSource('brirailroads');

      this.map.removeLayer('brirailstops');
      this.map.removeSource('brirailstops');

      this.map.removeLayer('oilpipelines');
      this.map.removeSource('oilpipelines');

      this.map.removeLayer('gaspipelines');
      this.map.removeSource('gaspipelines');

      this.map.removeLayer('ports');
      this.map.removeSource('ports');
    },
    loadMapLayers() {
      // add geojson sources
      this.map.addSource('brirailroads', {
        type: 'geojson',
        data: '/data/map/final/brirailroads.json',
      });
      this.map.addSource('brirailstops', {
        type: 'geojson',
        data: '/data/map/final/brirailstops.json',
      });
      this.map.addSource('oilpipelines', {
        type: 'geojson',
        data: '/data/map/final/oilpipelines.json',
      });
      this.map.addSource('gaspipelines', {
        type: 'geojson',
        data: '/data/map/final/gaspipelines.json',
      });
      this.map.addSource('ports', {
        type: 'geojson',
        data: '/data/map/issues/ports.json',
      });

      // get layer settings
      let layers = this.mapLayers(7);

      // add all layers
      layers.forEach(layerOption => {
        this.map.addLayer(layerOption);
      });
    },
    loadMapUIAndEvents(load = true) {
      if (load) {
        // events
        this.map.dragPan.enable();
        d3.select(this.map.getCanvas().parentElement).classed('mapboxgl-interactive', true);

        // add click and hover events to all layers
        this.map.on('mousemove', 'brirailroads', this.handleMapPopup);
        this.map.on('mouseleave', 'brirailroads', this.handleLayerExit);

        this.map.on('mousemove', 'brirailstops', this.handleMapPopup);
        this.map.on('mouseleave', 'brirailstops', this.handleLayerExit);

        this.map.on('mousemove', 'oilpipelines', this.handleMapPopup);
        this.map.on('mouseleave', 'oilpipelines', this.handleLayerExit);

        this.map.on('mousemove', 'gaspipelines', this.handleMapPopup);
        this.map.on('mouseleave', 'gaspipelines', this.handleLayerExit);

        this.map.on('mousemove', 'ports', this.handleMapPopup);
        this.map.on('mouseleave', 'ports', this.handleLayerExit);
      } else {
        // ui
        d3.select('#map-terrain-container .mapboxgl-custom-popup').style('display', 'none');

        // events
        this.map.dragPan.disable();
        d3.select(this.map.getCanvas().parentElement).classed('mapboxgl-interactive', false);

        // add click and hover events to all layers
        this.map.off('mousemove', 'brirailroads', this.handleMapPopup);
        this.map.off('mouseleave', 'brirailroads', this.handleLayerExit);

        this.map.off('mousemove', 'brirailstops', this.handleMapPopup);
        this.map.off('mouseleave', 'brirailstops', this.handleLayerExit);

        this.map.off('mousemove', 'oilpipelines', this.handleMapPopup);
        this.map.off('mouseleave', 'oilpipelines', this.handleLayerExit);

        this.map.off('mousemove', 'gaspipelines', this.handleMapPopup);
        this.map.off('mouseleave', 'gaspipelines', this.handleLayerExit);

        this.map.off('mousemove', 'ports', this.handleMapPopup);
        this.map.off('mouseleave', 'ports', this.handleLayerExit);
      }
    },
    handleLayerVisibility(layerId, visible) {
      const visibility = visible ? 'visible' : 'none';

      if (layerId === 'railroads') {
        this.map.setLayoutProperty('brirailroads', 'visibility', visibility);
        this.map.setLayoutProperty('brirailstops', 'visibility', visibility);
      } else {
        this.map.setLayoutProperty(layerId, 'visibility', visibility);
      }
    },
    handleMapPopup(e) {
      this.handleLayerEnter();

      // select popup element
      const popupEl = d3.select('#map-terrain-container .mapboxgl-custom-popup');

      // popup content
      let title = e.features[0].properties.Name;

      let category = e.features[0].properties.Subcategory === 'null' ? '' : e.features[0].properties.Subcategory;
      // let theCat = e.features[0].properties.Category === 'null' ? '' : e.features[0].properties.Category;
      // let category = `${subCat} ${theCat}`;

      let description =
        e.features[0].properties.CostUSbillions === 'null'
          ? 'Cost: Unavailable'
          : `Cost: US$ ${e.features[0].properties.CostUSbillions} Billion`;
      let description2 = e.features[0].properties.Status === 'null' ? '' : `Status: ${e.features[0].properties.Status}`;

      switch (e.features[0].layer.id) {
        case 'ports':
          category = e.features[0].properties.Subcategory;
          break;

        default:
          break;
      }

      // update popup el
      popupEl.style('display', 'block').style('transform', `translate(${e.point.x}px, ${e.point.y}px)`);

      // update popup content
      this.$store.dispatch('popup/updateTitle', title);
      this.$store.dispatch('popup/updateCategory', category);
      this.$store.dispatch('popup/updateDescription', description);
      this.$store.dispatch('popup/updateDescription2', description2);
    },
    handleLayerEnter() {
      this.map.getCanvas().style.cursor = 'pointer';
    },
    handleLayerExit() {
      this.map.getCanvas().style.cursor = '';
      d3.select('#map-terrain-container .mapboxgl-custom-popup').style('display', 'none');
    },
  },
};
</script>
